import React, { useEffect, useState } from 'react';
import api from '../lib/adminApi';
import { amount, dateTime, endpoint, message } from './api';
import { Empty, LoadState, Notice, Status } from './shared';

type HomeData = {
  asOf: string;
  registeredUsers: number;
  latestBills: Array<{
    id: string; title: string; payer: string; total: number | null;
    currency: string; status: string; createdAt: string;
  }>;
};

export default function LatestActivity({ refresh, onBill }: {
  refresh: number; onBill: (id: string) => void;
}) {
  const [data, setData] = useState<HomeData>();
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    let pending = false;
    const load = async (force = false) => {
      if (pending || document.hidden) return;
      pending = true;
      try {
        const response = await api.get(`${endpoint}/home`, {
          params: force ? { refresh: true } : {}, signal: controller.signal,
        });
        if (!controller.signal.aborted) { setData(response.data); setError(''); }
      } catch (e) {
        if (!controller.signal.aborted) setError(message(e));
      } finally { pending = false; }
    };
    void load(refresh > 0 || retry > 0);
    const poll = () => { void load(); };
    const timer = window.setInterval(poll, 30000);
    document.addEventListener('visibilitychange', poll);
    return () => {
      controller.abort(); window.clearInterval(timer);
      document.removeEventListener('visibilitychange', poll);
    };
  }, [refresh, retry]);

  return <section className="admin-live admin-panel" aria-labelledby="latest-bills-title">
    <div className="admin-live-heading">
      <div><h2 id="latest-bills-title">Latest bills</h2><p className="admin-muted">Updates every 30 seconds</p></div>
      <div className="admin-registered"><span>Registered users</span><strong>{data ? data.registeredUsers.toLocaleString() : '—'}</strong></div>
    </div>
    {!data && <LoadState loading={!error} error={error} retry={() => setRetry(n => n + 1)} />}
    {data && <>
      {error && <Notice danger><p>Updates paused. Showing the last successful refresh.</p><p>{error}</p><button className="admin-text-link" onClick={() => setRetry(n => n + 1)}>Try again</button></Notice>}
      {data.latestBills.length ? <ol className="admin-live-bills">
        {data.latestBills.map(bill => <li key={bill.id}>
          <button className="admin-live-bill" onClick={() => onBill(bill.id)}>
            <span className="admin-live-title">{bill.title}</span>
            <strong>{amount(bill.total, bill.currency)} <small>{bill.currency}</small></strong>
            <span className="admin-live-payer">Paid by {bill.payer}</span>
            <Status value={bill.status} />
            <time dateTime={bill.createdAt}>{dateTime(bill.createdAt)}</time>
          </button>
        </li>)}
      </ol> : <Empty title="No bills yet" detail="Recent bills will appear here." />}
      <p className="admin-muted">Updated {dateTime(data.asOf)} · Latest 10, including drafts. Test bills excluded.</p>
    </>}
  </section>;
}
